import { useState, useEffect, createContext, useContext, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import MatchList from "./pages/MatchList";
import LiveMatch from "./pages/LiveMatch";
import Leaderboard from "./pages/Leaderboard";
import LiveLeaderboard from "./pages/LiveLeaderboard";
import Terms from "./pages/Terms";
import LoadingScreen from "./components/LoadingScreen";
import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import { Toaster } from "./components/ui/sonner";

const CRM = lazy(() => import("./pages/crm/CRM"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const GoogleCallback = lazy(() => import("./pages/GoogleCallback"));

import { BACKEND_URL } from "@/lib/api";

// Auth Context for sharing auth state across components
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider Component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [cityId, setCityId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Decode JWT payload without a library (base64url → JSON)
  const decodeJwtPayload = (token) => {
    try {
      const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      return JSON.parse(atob(base64));
    } catch {
      return null;
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // Extract token and cityId from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      let urlToken = urlParams.get("token");
      let urlCityId = urlParams.get("cityId");

      // Defensive: some clients append cityId to token without & separator
      if (urlToken && urlToken.includes("cityId=")) {
        const parts = urlToken.split("cityId=");
        urlToken = parts[0].replace(/[&?]$/, "");
        if (!urlCityId) urlCityId = parts[1];
      }

      if (urlToken) {
        // Store token
        setToken(urlToken);
        setCityId(urlCityId);

        // Decode JWT client-side for instant rendering
        const payload = decodeJwtPayload(urlToken);
        if (payload?.sub) {
          const quickUser = {
            user_id: String(payload.sub),
            id: String(payload.sub),
            username: `User_${payload.sub}`,
          };
          setUser(quickUser);
          setIsAuthenticated(true);
          setLoading(false);

          // Clean URL immediately
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);

          // Validate with backend in the background and update with full user data
          authenticateWithToken(urlToken, urlCityId).then((authResult) => {
            if (authResult.success) {
              setUser(authResult.user);
            }
          });
          return;
        }

        // Fallback: if JWT decode fails, authenticate synchronously
        const authResult = await authenticateWithToken(urlToken, urlCityId);

        if (authResult.success) {
          setUser(authResult.user);
          setIsAuthenticated(true);
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        } else {
          setError(authResult.error);
        }
      } else {
        // No token provided - check localStorage
        const savedUser = localStorage.getItem("hudleUser");
        const savedToken = localStorage.getItem("hudleToken");

        if (savedUser && savedToken) {
          setUser(JSON.parse(savedUser));
          setToken(savedToken);
          setIsAuthenticated(true);
        } else {
          setError("No authentication token provided");
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const authenticateWithToken = async (jwtToken, city) => {
    try {
      const url = city ? `${BACKEND_URL}/api/auth/me?cityId=${city}` : `${BACKEND_URL}/api/auth/me`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || "Authentication failed" };
      }

      const data = await response.json();

      // Save to localStorage for persistence
      localStorage.setItem("hudleUser", JSON.stringify(data.user));
      localStorage.setItem("hudleToken", jwtToken);
      if (city) localStorage.setItem("hudleCityId", city);

      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Helper function to get auth headers for API calls
  const getAuthHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  };

  // Authenticated fetch helper
  const authFetch = async (url, options = {}) => {
    const headers = {
      ...getAuthHeaders(),
      ...options.headers,
    };
    return fetch(url, { ...options, headers });
  };

  const value = {
    user,
    token,
    cityId,
    isAuthenticated,
    loading,
    error,
    getAuthHeaders,
    authFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function App() {
  const [showLoading, setShowLoading] = useState(true);

  const handleLoadComplete = () => {
    setShowLoading(false);
  };

  // Remove the inline HTML splash once React has mounted
  useEffect(() => {
    const el = document.getElementById("initial-splash");
    if (el) el.remove();
  }, []);

  // Skip splash screen for public pages
  const isPublicPage =
    window.location.pathname === "/terms" || window.location.pathname.startsWith("/admin");

  return (
    <AuthProvider>
      {showLoading && !isPublicPage ? (
        <LoadingScreen onLoadComplete={handleLoadComplete} minDuration={1500} />
      ) : (
        <AppContent />
      )}
    </AuthProvider>
  );
}

function AppContent() {
  return (
    <div className="App min-h-screen bg-gradient-to-br from-[hsl(var(--hudle-primary-100))] via-white to-[hsl(var(--hudle-secondary-100))]">
      <Toaster position="top-center" expand={false} />
      <BrowserRouter>
        <AdminAuthProvider>
          <Suspense
            fallback={
              <div
                className="min-h-screen flex items-center justify-center"
                style={{ backgroundColor: "#0f0f0f" }}
              >
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#01ff9a] mx-auto"></div>
              </div>
            }
          >
            <Routes>
              {/* CRM routes — no JWT required, uses Google OAuth */}
              <Route path="/crm" element={<ProtectedCRM />} />
              <Route path="/admin/auth/google/callback" element={<GoogleCallback />} />

              {/* Public routes */}
              <Route path="/terms" element={<Terms />} />

              {/* App routes — require Hudle JWT */}
              <Route path="/*" element={<AuthenticatedRoutes />} />
            </Routes>
          </Suspense>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

function AuthenticatedRoutes() {
  const { user, loading, error } = useAuth();

  if (loading || !user) {
    // Only show error text; keep background transparent to avoid a black flash
    // between splash and app content
    if (error) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-sm text-red-500">Error: {error}</p>
        </div>
      );
    }
    return null;
  }

  return (
    <Routes>
      <Route path="/" element={<MatchList user={user} />} />
      <Route path="/match/:matchId" element={<LiveMatch user={user} />} />
      <Route path="/leaderboard/:matchId" element={<Leaderboard user={user} />} />
      <Route path="/live-leaderboard/:matchId" element={<LiveLeaderboard user={user} />} />
    </Routes>
  );
}

function ProtectedCRM() {
  const { isAdminAuthenticated, loading } = useAdminAuth();

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#0f0f0f" }}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#01ff9a] mx-auto"></div>
      </div>
    );
  }

  if (!isAdminAuthenticated) {
    return <AdminLogin />;
  }

  return <CRM />;
}

export default App;
